/*
 * RK native size guide. Pairs with snippets/rk-size-guide.liquid.
 *
 * Each [data-rk-size-guide] root carries the product's AS Colour style code,
 * the URL of the size-charts.json theme asset, and the URL of the
 * how-to-measure diagram asset. On first open we fetch the JSON, find the
 * chart for this style code, and render the branded modal. The JSON is
 * fetched once and cached for the page lifetime.
 *
 * The "how to measure" panel only renders for upper-body garments
 * (chart.category === "top") and starts collapsed.
 *
 * The column matching the customer's currently-selected size is highlighted
 * automatically, and stays in sync if they change size while the modal is
 * open. Chart size headers (AS Colour codes like XSM/SML/MED) are matched to
 * the product's variant sizes (XS/S/M) via normaliseSize().
 */
(function () {
  "use strict";

  var chartsPromise = null;

  function loadCharts(url) {
    if (!chartsPromise) {
      chartsPromise = fetch(url).then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      });
    }
    return chartsPromise;
  }

  function escapeHtml(value) {
    var div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  // Collapse a size label to a canonical token so AS Colour chart headers
  // (XSM, SML, MED, LRG, XLG) match the product's variant sizes (XS, S, M...).
  var SIZE_ALIASES = {
    XSM: "XS", XSMALL: "XS", EXTRASMALL: "XS",
    SML: "S", SMALL: "S",
    MED: "M", MEDIUM: "M",
    LRG: "L", LARGE: "L",
    XLG: "XL", XLARGE: "XL", EXTRALARGE: "XL",
    XXL: "2XL", "2X": "2XL", XXLARGE: "2XL",
    XXXL: "3XL", "3X": "3XL",
    XXXXL: "4XL", "4X": "4XL",
    ONESIZE: "OS", OS: "OS", OSFA: "OS",
  };
  function normaliseSize(value) {
    var t = String(value == null ? "" : value).toUpperCase().replace(/[\s\-_/.]/g, "");
    return SIZE_ALIASES[t] || t;
  }

  // The measurement steps shown below the how-to-measure diagram. These map
  // to the two pink guide lines on rk-size-guide-howto.svg: the horizontal
  // chest line (body width) and the vertical line (body length).
  var HOWTO_STEPS = [
    ["1", "Body width", "Lay the garment flat. Measure straight across the chest, seam to seam."],
    ["2", "Body length", "Measure from the highest point of the shoulder straight down to the hem."],
  ];

  function buildTable(chart) {
    var head = "<tr><th>Size</th>";
    (chart.sizes || []).forEach(function (size, i) {
      head += '<th data-col="' + i + '">' + escapeHtml(size) + "</th>";
    });
    head += "</tr>";

    var body = "";
    (chart.rows || []).forEach(function (row) {
      body += "<tr><th>" + escapeHtml(row.label) + "</th>";
      (row.values || []).forEach(function (value, i) {
        body += '<td data-col="' + i + '">' + escapeHtml(value) + "</td>";
      });
      body += "</tr>";
    });

    return (
      '<div class="sg-table-scroll"><table class="rk-size-guide__table" data-style="zebra">' +
      "<thead>" + head + "</thead><tbody>" + body + "</tbody></table></div>"
    );
  }

  function buildHowTo(chart, howtoUrl) {
    var steps = "";
    HOWTO_STEPS.forEach(function (s) {
      steps +=
        '<li><span class="num">' + s[0] + "</span>" +
        "<span><b>" + escapeHtml(s[1]) + ".</b> " + escapeHtml(s[2]) + "</span></li>";
    });
    var diagram = howtoUrl
      ? '<img src="' + encodeURI(howtoUrl) + '" alt="How to measure diagram" loading="lazy">'
      : "";
    // Starts collapsed - the customer expands it if they want it.
    return (
      '<section class="sg-howto collapsed" data-rk-sg-howto>' +
      '<button type="button" class="sg-howto-head" data-rk-sg-howto-toggle aria-expanded="false">' +
      "<span>How to measure your " + escapeHtml((chart.name || "garment").toLowerCase()) + "</span>" +
      '<span class="chev" aria-hidden="true">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" ' +
      'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5 L6 8 L9 5"/></svg>' +
      "</span></button>" +
      '<div class="sg-howto-body">' +
      '<div class="sg-howto-diagram">' + diagram + "</div>" +
      '<ol class="sg-howto-list">' + steps + "</ol>" +
      "</div></section>"
    );
  }

  function init(root) {
    if (root.dataset.rkSgReady === "1") return;
    root.dataset.rkSgReady = "1";

    var openBtn = root.querySelector("[data-rk-sg-open]");
    var overlay = root.querySelector("[data-rk-sg-overlay]");
    var closeBtn = root.querySelector("[data-rk-sg-close]");
    var titleEl = root.querySelector("[data-rk-sg-title]");
    var content = root.querySelector("[data-rk-sg-content]");
    var styleCode = (root.dataset.styleCode || "").trim();
    var chartsUrl = root.dataset.chartsUrl;
    var howtoUrl = root.dataset.howtoUrl;
    if (!openBtn || !overlay || !content || !chartsUrl) return;

    // Portal the overlay onto <body>. Left where the snippet renders it, the
    // overlay is nested inside the product section's .section-content (which
    // has z-index:10), so its own z-index is trapped in that low stacking
    // context and the site header renders on top of the dimmed overlay.
    if (overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }

    var rendered = false;
    var currentChart = null;

    // Read the customer's currently-selected size from the product form.
    // Looks at every variant selector and returns the value that matches one
    // of this chart's size columns (so it ignores Colour, Quantity, etc.).
    function getSelectedSize(chart) {
      var normChart = (chart.sizes || []).map(normaliseSize);
      var selectors = document.querySelectorAll("[data-single-option-selector]");
      for (var i = 0; i < selectors.length; i++) {
        var el = selectors[i];
        var val = null;
        if (el.tagName === "SELECT") val = el.value;
        else if (el.type === "radio" && el.checked) val = el.value;
        if (val && normChart.indexOf(normaliseSize(val)) !== -1) return val;
      }
      return null;
    }

    // Refresh the "you picked X" pill + the highlighted table column to match
    // the current size selection. Safe to call any time after draw().
    function refreshSelection() {
      if (!currentChart) return;
      var table = content.querySelector(".rk-size-guide__table");
      var pill = content.querySelector("[data-rk-sg-selected]");
      if (!table) return;

      var prev = table.querySelectorAll(".col-on");
      for (var p = 0; p < prev.length; p++) prev[p].classList.remove("col-on");

      var selected = getSelectedSize(currentChart);
      if (!selected) {
        if (pill) pill.innerHTML = "";
        return;
      }
      var norm = normaliseSize(selected);
      var idx = -1;
      for (var i = 0; i < currentChart.sizes.length; i++) {
        if (normaliseSize(currentChart.sizes[i]) === norm) { idx = i; break; }
      }
      if (pill) {
        pill.innerHTML = idx >= 0
          ? "You picked <b>" + escapeHtml(selected) + "</b>"
          : "";
      }
      if (idx < 0) return;
      var cells = table.querySelectorAll('[data-col="' + idx + '"]');
      for (var c = 0; c < cells.length; c++) cells[c].classList.add("col-on");
    }

    function open() {
      overlay.classList.add("is-open");
      document.body.style.overflow = "hidden";
      if (!rendered) draw();
      else refreshSelection();
    }

    function close() {
      overlay.classList.remove("is-open");
      document.body.style.overflow = "";
    }

    function draw() {
      rendered = true;
      content.innerHTML =
        '<p class="rk-size-guide__status">Loading size guide&hellip;</p>';
      loadCharts(chartsUrl)
        .then(function (charts) {
          var chart = charts[styleCode];
          if (!chart) {
            content.innerHTML =
              '<p class="rk-size-guide__status">Sorry, the size guide for this product isn\'t available right now.</p>';
            return;
          }
          currentChart = chart;
          if (titleEl) {
            titleEl.textContent = (chart.name || "Garment") + " size guide";
          }
          var html = '<div class="sg-selected-slot" data-rk-sg-selected></div>';
          html += buildTable(chart);
          if (chart.note) {
            html += '<p class="rk-size-guide__note">' + escapeHtml(chart.note) + "</p>";
          }
          // How-to-measure panel: upper-body garments only.
          if (chart.category === "top") {
            html += buildHowTo(chart, howtoUrl);
          }
          content.innerHTML = html;

          // Wire the how-to-measure collapse toggle.
          var howto = content.querySelector("[data-rk-sg-howto]");
          var toggle = content.querySelector("[data-rk-sg-howto-toggle]");
          if (howto && toggle) {
            toggle.addEventListener("click", function () {
              var collapsed = howto.classList.toggle("collapsed");
              toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
            });
          }

          refreshSelection();
        })
        .catch(function () {
          rendered = false;
          chartsPromise = null;
          content.innerHTML =
            '<p class="rk-size-guide__status">Couldn\'t load the size guide. Please close this and try again.</p>';
        });
    }

    openBtn.addEventListener("click", open);
    if (closeBtn) closeBtn.addEventListener("click", close);
    overlay.addEventListener("mousedown", function (event) {
      if (event.target === overlay) close();
    });
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && overlay.classList.contains("is-open")) close();
    });
    // Keep the highlighted column in sync if the customer changes size.
    document.addEventListener("change", function (event) {
      if (event.target && event.target.matches &&
          event.target.matches("[data-single-option-selector]")) {
        refreshSelection();
      }
    });
  }

  function boot() {
    var roots = document.querySelectorAll("[data-rk-size-guide]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
